import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, ChevronDown, ChevronUp, Loader2, Trash2, CheckCircle,
  Copy, FileText,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

/**
 * Duplicate-invoices banner for the Bandeja.
 * Fetches groups from `/finance/duplicates` and renders a collapsible card
 * with inline resolve actions.
 */
export default function DuplicatesBanner({ restaurantId, onResolved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});   // groupIdx -> bool
  const [busy, setBusy] = useState(null);          // docId currently being deleted

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.getDuplicateDocuments(restaurantId);
      setData(r);
      // auto-expand the first group on first load
      if (r?.groups?.length) setExpanded({ 0: true });
    } catch (e) {
      console.warn("Duplicate detection failed:", e);
    } finally { setLoading(false); }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const deleteDoc = async (docId) => {
    setBusy(docId);
    try {
      await api.resolveDuplicates(restaurantId, [docId]);
      toast.success("Documento duplicado eliminado");
      await load();
      onResolved?.();
    } catch (e) {
      toast.error(e.message || "Error al eliminar");
    } finally { setBusy(null); }
  };

  const autoResolveAll = async () => {
    // Delete all duplicates across all groups with confidence ≥ 0.9 (safe)
    const safeDupes = (data?.groups || [])
      .filter(g => g.confidence >= 0.9)
      .flatMap(g => g.duplicates.map(d => d.document_id));
    if (!safeDupes.length) return;
    if (!window.confirm(`Se eliminarán ${safeDupes.length} duplicados exactos. ¿Continuar?`)) return;
    setBusy("bulk");
    try {
      const r = await api.resolveDuplicates(restaurantId, safeDupes);
      toast.success(`${r.deleted} duplicados eliminados`);
      await load();
      onResolved?.();
    } catch (e) {
      toast.error(e.message || "Error");
    } finally { setBusy(null); }
  };

  if (loading) return null;
  if (!data || data.count === 0) return null;

  const hasExactMatches = data.groups.some(g => g.confidence >= 0.9);

  return (
    <div className="rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4"
         data-testid="duplicates-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="h-9 w-9 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
            <Copy className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-amber-900 text-sm flex items-center gap-2">
              {data.total_duplicates} posibles duplicados detectados
              <span className="text-[11px] font-normal text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                {data.count} {data.count === 1 ? "grupo" : "grupos"}
              </span>
            </h3>
            <p className="text-xs text-amber-800/80 mt-0.5">
              Hemos encontrado facturas que parecen la misma cargada dos veces (p.ej. una desde la Bandeja y otra desde Inventario).
              Revisa cada grupo y elimina los duplicados para que el P&L no los sume doble.
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {hasExactMatches && (
            <Button
              size="sm"
              onClick={autoResolveAll}
              disabled={busy === "bulk"}
              className="bg-amber-600 hover:bg-amber-700 h-8"
              data-testid="duplicates-autoresolve"
            >
              {busy === "bulk"
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                : <CheckCircle className="h-3.5 w-3.5 mr-1" />}
              Resolver exactos
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={load} className="h-8 text-amber-700">
            Revisar de nuevo
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {data.groups.map((group, idx) => {
          const isOpen = !!expanded[idx];
          const confPct = Math.round(group.confidence * 100);
          const confTone =
            group.confidence >= 0.9 ? "bg-red-100 text-red-800 border-red-200" :
            group.confidence >= 0.7 ? "bg-orange-100 text-orange-800 border-orange-200" :
            "bg-muted text-foreground border-border";
          return (
            <div key={group.signature}
                 className="bg-card border border-amber-200 rounded-lg overflow-hidden shadow-sm"
                 data-testid={`duplicate-group-${idx}`}>
              <button
                type="button"
                className="w-full px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-amber-50/50 transition"
                onClick={() => setExpanded({ ...expanded, [idx]: !isOpen })}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                  <div className="text-left min-w-0 flex-1">
                    <div className="text-sm font-semibold text-foreground truncate">
                      {group.primary.supplier_name || "Sin proveedor"}
                      <span className="ml-2 text-muted-foreground font-normal">· {formatCurrency(group.primary.total_amount)}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">{group.reason}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${confTone}`}>
                    {confPct}%
                  </span>
                  {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-amber-100 divide-y divide-slate-100">
                  <DuplicateRow doc={group.primary} isPrimary busy={busy} onDelete={deleteDoc} />
                  {group.duplicates.map(d => (
                    <DuplicateRow key={d.document_id} doc={d} busy={busy} onDelete={deleteDoc} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DuplicateRow({ doc, isPrimary, busy, onDelete }) {
  return (
    <div className="px-3 py-2.5 flex items-center gap-3 text-sm"
         data-testid={`duplicate-row-${doc.document_id}`}>
      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-foreground truncate">
            {doc.filename || doc.invoice_number || doc.document_id}
          </span>
          {isPrimary ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
              A conservar
            </span>
          ) : (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
              Duplicado
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          {fmtDate(doc.issue_date || doc.created_at)}
          {doc.invoice_number ? ` · nº ${doc.invoice_number}` : ""}
          {doc.notes ? ` · ${doc.notes.slice(0, 60)}` : ""}
        </div>
      </div>
      {!isPrimary && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(doc.document_id)}
          disabled={busy === doc.document_id || busy === "bulk"}
          className="h-7 text-red-700 hover:text-red-700 hover:bg-red-50 shrink-0"
          data-testid={`duplicate-delete-${doc.document_id}`}
        >
          {busy === doc.document_id
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <><Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar</>}
        </Button>
      )}
    </div>
  );
}
